import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, Wrench, Hammer, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const services = [
    { icon: Flame, label: 'Welding' },
    { icon: Wrench, label: 'Mechanics' },
    { icon: Hammer, label: 'Handyman' },
    { icon: Sparkles, label: 'Cleaning' },
  ];

  return (
    <section className="hero-section noise-bg" data-testid="hero-section">
      {/* Background Image */}
      <div className="hero-bg">
        <img
          src="https://images.unsplash.com/photo-1745448797900-35d08e85e9db"
          alt="Industrial welding background"
          loading="eager"
        />
      </div>
      <div className="hero-overlay" />

      {/* Floating Orbs */}
      <div className="orb orb-cyan w-[600px] h-[600px] -top-40 -left-40 opacity-30" />
      <div className="orb orb-teal w-[400px] h-[400px] bottom-20 right-10 opacity-40" />

      {/* Content */}
      <div className="hero-content w-full px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:justify-between gap-10 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-[#00F0FF] animate-pulse" />
              <span className="text-sm text-gray-300">Professional Trade Services</span>
            </motion.div>

            {/* Main Heading */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-6">
              <span className="text-white">UNLEASH THE</span>
              <br />
              <span className="gradient-text animate-glitch">OMNIBEAST</span>
            </h1>

            {/* Subheading */}
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl leading-relaxed">
              From welding sparks to spotless spaces. We handle cleaning, handyman work, 
              mechanics, and custom fabrication with precision and power.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <Link to="/booking">
                <Button
                  data-testid="hero-cta-primary"
                  className="btn-primary rounded-none px-8 py-6 text-lg font-heading tracking-wide group"
                >
                  SUMMON THE BEAST
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className="btn-outline rounded-none px-8 py-6 text-lg font-heading tracking-wide"
                >
                  VIEW SERVICES
                </Button>
              </Link>
            </div>

            {/* Service Icons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap gap-8"
            >
              {services.map((service, index) => (
                <motion.div
                  key={service.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-3 group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-lg glass flex items-center justify-center group-hover:glow-border transition-all duration-300">
                    <service.icon className="w-6 h-6 text-[#00F0FF]" />
                  </div>
                  <span className="text-gray-400 group-hover:text-white transition-colors font-medium">
                    {service.label}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Brand Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="shrink-0 w-64 sm:w-80 lg:w-[30rem]"
          >
            <img
              src="/omnibeast-wolf.png"
              alt="OmniBeast Services logo"
              data-testid="hero-logo"
              className="w-full h-auto drop-shadow-[0_0_50px_rgba(0,240,255,0.4)]"
            />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-6 h-10 rounded-full border-2 border-[#00F0FF]/50 flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
